import { useContext, useMemo } from "react";
import { Tag } from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DictContext } from "@/layout/context/DictContext";
import SText from "@/styles/SText";
import { EquipmentEnumItem, MaintainUpcoming } from "@/types/equipment";

// 保养到期列 Hook 所需的设备状态枚举。
interface UseMaintainUpcomingColumnProps {
  statusItems: EquipmentEnumItem[];
}

// 保养与购置信息统一以短横线表示空值。
const renderText = (value?: string | number | null) => (
  <SText
    text={value === null || value === undefined || value === ""
      ? "-"
      : String(value)}
    style={{ width: "100%" }}
  />
);

// 生成保养到期列表列，展开设备档案并格式化字典与时间字段。
const useMaintainUpcomingColumn = ({
  statusItems,
}: UseMaintainUpcomingColumnProps): ColumnsType<MaintainUpcoming> => {
  const { t } = useTranslation();
  const { currencys } = useContext(DictContext);

  return useMemo<ColumnsType<MaintainUpcoming>>(() => [
    {
      title: t("equipment.equipmentNumber"),
      key: "number",
      width: 150,
      fixed: "left",
      render: (_, item) => renderText(item.equipment.number),
    },
    {
      title: t("equipment.equipmentName"),
      key: "name",
      width: 180,
      fixed: "left",
      render: (_, item) => renderText(item.equipment.name),
    },
    {
      title: t("equipment.brand"),
      key: "brand",
      width: 130,
      render: (_, item) => renderText(item.equipment.brand),
    },
    {
      title: t("equipment.model"),
      key: "model",
      width: 140,
      render: (_, item) => renderText(item.equipment.model),
    },
    {
      title: t("equipment.serialNo"),
      key: "serialNo",
      width: 160,
      render: (_, item) => renderText(item.equipment.serialNo),
    },
    // 购置信息使用分组表头，币种按字典文案显示。
    {
      title: t("equipment.purchaseInfo"),
      key: "purchaseInfo",
      children: [
        {
          title: t("equipment.purchasePrice"),
          key: "purchasePrice",
          width: 130,
          render: (_, item) => renderText(item.equipment.purchasePrice),
        },
        {
          title: t("equipment.purchaseCurrency"),
          key: "purchaseCurrency",
          width: 130,
          render: (_, item) => {
            const value = item.equipment.purchaseCurrency;
            return renderText(
              value === null
                ? null
                : currencys.find(({ key }) => String(key) === String(value))
                  ?.desc ?? value,
            );
          },
        },
        {
          title: t("equipment.purchaseAt"),
          key: "purchaseAt",
          width: 120,
          render: (_, item) => renderText(
            item.equipment.purchaseAt === null
              ? null
              : dayjs(item.equipment.purchaseAt).format("YYYY-MM-DD"),
          ),
        },
      ],
    },
    {
      title: t("equipment.equipmentCategory"),
      key: "category",
      width: 150,
      render: (_, item) => renderText(item.equipment.category?.name),
    },
    // 状态文案来自接口枚举，未知值回退为原始数值。
    {
      title: t("equipment.equipmentStatus"),
      key: "status",
      width: 120,
      render: (_, item) => (
        <SText
          text={statusItems.find(
            ({ v }) => v === item.equipment.status,
          )?.desc ?? String(item.equipment.status)}
          tagMode
          style={{ textAlign: "left" }}
          tagStyle={{ marginInlineEnd: 0 }}
        />
      ),
    },
    {
      title: t("equipment.keeperOrg"),
      key: "keeperOrg",
      width: 160,
      render: (_, item) => renderText(item.equipment.keeperOrg?.name),
    },
    {
      title: t("equipment.ownedOrg"),
      key: "org",
      width: 160,
      render: (_, item) => renderText(item.equipment.org?.name),
    },
    {
      title: t("equipment.location"),
      key: "location",
      width: 150,
      render: (_, item) => renderText(item.equipment.location),
    },
    {
      title: t("equipment.placeLocation"),
      key: "placeLocation",
      width: 150,
      render: (_, item) => renderText(item.equipment.placeLocation),
    },
    {
      title: t("equipment.supplier"),
      key: "supplier",
      width: 150,
      render: (_, item) => renderText(item.equipment.supplier),
    },
    {
      title: t("equipment.maintainDays"),
      key: "maintainDays",
      width: 150,
      render: (_, item) => renderText(item.equipment.maintainDays),
    },
    {
      title: t("equipment.maintainThreshold"),
      key: "maintainThreshold",
      width: 180,
      render: (_, item) => renderText(item.equipment.maintainThreshold),
    },
    {
      title: t("equipment.remainDays"),
      dataIndex: "remainDays",
      width: 110,
      render: renderText,
    },
    {
      title: t("equipment.remainThreshold"),
      dataIndex: "remainThreshold",
      width: 140,
      render: renderText,
    },
    {
      title: t("equipment.lastMaintainAt"),
      key: "lastMaintainAt",
      width: 160,
      render: (_, item) => renderText(
        item.equipment.lastMaintainAt === null
          ? null
          : dayjs(item.equipment.lastMaintainAt).format("YYYY-MM-DD HH:mm"),
      ),
    },
    // 天数和使用量是相互独立的到期维度，分别固定在右侧展示。
    {
      title: t("equipment.dueByDays"),
      dataIndex: "dueByDays",
      width: 130,
      fixed: "right",
      render: (isDue: boolean) => (
        <Tag color={isDue ? "red" : "green"}>
          {t(isDue ? "equipment.due" : "equipment.notDue")}
        </Tag>
      ),
    },
    {
      title: t("equipment.dueByThreshold"),
      dataIndex: "dueByThreshold",
      width: 140,
      fixed: "right",
      render: (isDue: boolean) => (
        <Tag color={isDue ? "red" : "green"}>
          {t(isDue ? "equipment.due" : "equipment.notDue")}
        </Tag>
      ),
    },
  ], [currencys, statusItems, t]);
};

export default useMaintainUpcomingColumn;
